import React, { useState, useEffect, useCallback } from "react";
import {
    Grid,
    Card,
    Button,
    Backdrop,
    CircularProgress,
    Alert,
    Snackbar,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import CustomTable from "components/CustomTable/CustomTable";
import ConfirmDialog from "components/ConfirmDialog/ConfirmDialog";
import apiRequest from "../../apiRequest";
import { endPoints } from "../../apis";

import PaginationControls from "components/PaginationControls/PaginationControls";
// import AgreementDetails from "./AgreementDetails";
import AddAgreementModal from "./AddAgreementModal";

function AgreementTemplate() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [agreements, setAgreements] = useState([]);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    // Modal states
    const [openAddModal, setOpenAddModal] = useState(false);
    const [editingAgreement, setEditingAgreement] = useState(null);

    // Confirm Dialog state
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [agreementToDelete, setAgreementToDelete] = useState(null);

    const fetchAgreements = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const response = await apiRequest.get(endPoints.GET_AGREEMENT_TEMPLATES, { page });
            if (response.success) {
                setAgreements(response.data.items || response.data || []);
                if (response.data.pagination) {
                    setTotalPages(response.data.pagination.total_pages);
                    setCurrentPage(response.data.pagination.current_page);
                    setTotalItems(response.data.pagination.totalItems);
                }
            } else {
                setAgreements([]);
            }
        } catch (err) {
            console.error("Error fetching agreements:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAgreements(currentPage);
    }, [fetchAgreements, currentPage]);

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleOpenAddModal = () => {
        setOpenAddModal(true);
    };

    const handleCloseAddModal = () => {
        setOpenAddModal(false);
        setEditingAgreement(null);
    };

    const handleAddSuccess = (message) => {
        setSuccess(message);
        fetchAgreements(currentPage);
    };

    // View Details
    const handleViewDetails = (row) => {
        const printWindow = window.open("", "_blank");
        if (!printWindow) {
            setError("New tab could not be opened. Please check your browser settings.");
            return;
        }

        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>${row.name}</title>
                <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap">
                <style>
                    body { 
                        font-family: 'Roboto', sans-serif; 
                        margin: 0; 
                        padding: 0; 
                        background-color: #f0f2f5; 
                        display: flex; 
                        flex-direction: column; 
                        align-items: center; 
                    }
                    .toolbar { 
                        width: 100%; 
                        background: #f8f9fa; 
                        padding: 15px 40px; 
                        display: flex; 
                        justify-content: flex-end; 
                        border-bottom: 1px solid #dee2e6;
                        position: sticky; 
                        top: 0; 
                        z-index: 100;
                        box-sizing: border-box;
                    }
                    .download-btn {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        padding: 8px 20px;
                        border: 1.5px solid #7c83fd;
                        background: white;
                        color: #1a1a1a;
                        border-radius: 6px;
                        font-weight: 500;
                        cursor: pointer;
                        transition: all 0.2s;
                        font-size: 16px;
                    }
                    .download-btn:hover {
                        background: #7c83fd;
                        color: white;
                    }
                    .document-wrapper {
                        width: 100%;
                        display: flex;
                        justify-content: center;
                        padding: 20px;
                        box-sizing: border-box;
                    }
                    .document-container {
                        width: 100%;
                        max-width: 100%;
                        min-height: 297mm;
                        background: white;
                        padding: 40px 60px;
                        box-shadow: 0 0 15px rgba(0,0,0,0.1);
                        box-sizing: border-box;
                    }
                    .header { border-bottom: 2px solid #eee; margin-bottom: 20px; padding-bottom: 20px; }
                    .title { font-size: 24px; font-weight: bold; margin: 0; }
                    .description { color: #666; margin-top: 5px; }
                    .content { margin-top: 30px; }
                    
                    @media print {
                        body { background: white; }
                        .toolbar { display: none; }
                        .document-wrapper { padding: 0; }
                        .document-container { box-shadow: none; max-width: none; padding: 0; }
                    }
                </style>
                <script src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"></script>
            </head>
            <body>
                <div class="toolbar">
                    <button class="download-btn" id="downloadBtn">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v4"></path>
                            <polyline points="7 10 12 15 17 10"></polyline>
                            <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                        Download PDF
                    </button>
                </div>
                <div class="document-wrapper">
                    <div class="document-container" id="agreement-content">
                        <div class="header">
                            <h1 class="title">${row.name}</h1>
                            ${row.description ? `<p class="description">${row.description}</p>` : ""}
                        </div>
                        <div class="content">
                            ${row.html_content || "No content available."}
                        </div>
                    </div>
                </div>
                <script>
                    document.getElementById('downloadBtn').addEventListener('click', function() {
                        const element = document.getElementById('agreement-content');
                        const opt = {
                            margin:       10,
                            filename:     '${row.name.replace(/\s+/g, '_')}.pdf',
                            image:        { type: 'jpeg', quality: 0.98 },
                            html2canvas:  { scale: 2 },
                            jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' }
                        };
                        html2pdf().set(opt).from(element).save();
                    });
                </script>
            </body>
            </html>
        `;

        printWindow.document.open();
        printWindow.document.write(htmlContent);
        printWindow.document.close();
    };

    // Edit Click Handler
    const handleEditClick = (row) => {
        setEditingAgreement(row);
        setOpenAddModal(true);
    };

    // Delete Click Handler
    const handleDeleteClick = (row) => {
        setAgreementToDelete(row);
        setOpenConfirmDialog(true);
    };

    // Confirm Delete Handler
    const handleConfirmDelete = async () => {
        if (!agreementToDelete) return;

        setLoading(true);
        setSuccess(null);
        setError(null);

        try {
            const response = await apiRequest.delete(`${endPoints.DELETE_AGREEMENT_TEMPLATE}/${agreementToDelete.id}`);
            if (response.success) {
                setSuccess("Template deleted successfully.");
                fetchAgreements(currentPage);
                setOpenConfirmDialog(false);
                setAgreementToDelete(null);
            } else {
                setError(response.message || "Failed to delete template.");
            }
        } catch (err) {
            console.error("Error deleting template:", err);
            setError("An error occurred while deleting the template.");
        } finally {
            setLoading(false);
        }
    };

    const handleCloseConfirmDialog = () => {
        setOpenConfirmDialog(false);
        setAgreementToDelete(null);
    };


    const columns = [
        { key: "name", label: "Name" },
        { key: "description", label: "Description" },
        // Add createAt if available
    ];

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">
                                    Agreement Templates
                                </MDTypography>
                                <Button
                                    variant="contained"
                                    onClick={handleOpenAddModal}
                                    sx={{
                                        backgroundColor: "white !important",
                                        color: "#1A73E8 !important",
                                        "&:hover": {
                                            backgroundColor: "#f0f0f0 !important",
                                        },
                                    }}
                                >
                                    Add Agreement
                                </Button>
                            </MDBox>
                            <MDBox p={3}>
                                <CustomTable
                                    data={agreements}
                                    columns={columns}
                                    onViewDetails={handleViewDetails}
                                    onEditClick={handleEditClick}
                                    onDeleteClick={handleDeleteClick}
                                />
                                {agreements.length > 0 && (
                                    <PaginationControls
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        totalItems={totalItems}
                                        onPageChange={handlePageChange}
                                    />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            <Footer />

            {/* Add Agreement Modal */}
            <AddAgreementModal
                open={openAddModal}
                onClose={handleCloseAddModal}
                onSuccess={handleAddSuccess}
                editingData={editingAgreement}
            />

            {/* Confirm Delete Dialog */}
            <ConfirmDialog
                open={openConfirmDialog}
                onClose={handleCloseConfirmDialog}
                onConfirm={handleConfirmDelete}
                title="Delete Template?"
                description={`Are you sure you want to delete "${agreementToDelete?.name}"? This action cannot be undone.`}
                confirmText="Delete"
                confirmColor="error"
            />

            {/* Loading Backdrop */}
            <Backdrop
                sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={loading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            {/* Snackbar */}
            <Snackbar
                open={!!error || !!success}
                autoHideDuration={4000}
                onClose={() => {
                    setError(null);
                    setSuccess(null);
                }}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert
                    onClose={() => {
                        setError(null);
                        setSuccess(null);
                    }}
                    severity={error ? "error" : "success"}
                    sx={{ width: "100%" }}
                >
                    {error || success}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
}

export default AgreementTemplate;
